import { Dtos, type SuretyAccountId } from "@inrev/common";
import { useFlags } from "@portal/providers/GlobalFlagsProvider";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAuthenticationError, logError, normalizeToError } from "../../../utils/error";
import { useRequest } from "../../../utils/request";
import { bondRequestPreviewsQueryKey } from "../../bond-request/bond-request.keys";
import { accountPreviewsQueryKey } from "../../previews/previews.keys";

export const useCreateAccount = () => {
	const { post } = useRequest();
	const queryClient = useQueryClient();
	const { showErrorFlag } = useFlags();

	const mutation = useMutation({
		mutationKey: ["createAccount"],
		mutationFn: async ({
			data,
		}: {
			data: Dtos.SuretyAccount.Create.Request;
			onSuccess: (accountId: SuretyAccountId) => void;
			onError?: (error: unknown) => void;
		}) => {
			return await post<Dtos.SuretyAccount.Create.Response>("/v2/surety/accounts", data);
		},
		onSuccess: (response, { onSuccess }) => {
			queryClient.refetchQueries({ queryKey: accountPreviewsQueryKey() });
			queryClient.invalidateQueries({ queryKey: bondRequestPreviewsQueryKey() });
			onSuccess(response.id);
		},
		onError: (error, { onError }) => {
			const err = normalizeToError(error);
			logError(err);
			if (!isAuthenticationError(err)) {
				showErrorFlag({
					heading: "Submission Failed",
					message: "We couldn't submit this account. Please try again.",
				});
			}
			onError?.(error);
		},
	});

	return {
		createAccount: mutation.mutate,
		createAccountIsLoading: mutation.isPending,
	};
};
